import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, fmtTime, TOPICS, type Published, type SpreadRow } from "../api";

export function Spreads() {
  const [rows, setRows] = useState<SpreadRow[]>();
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [topic, setTopic] = useState("all");
  const [newId, setNewId] = useState("");
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [msg, setMsg] = useState<{ ok: boolean; text: string }>();
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();
  const load = () => api<{ spreads: SpreadRow[] }>("/admin/spreads").then((r) => setRows(r.spreads)).catch((e: Error) => setErr(e.message));
  useEffect(() => { void load(); }, []);

  const toggle = (id: string) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const bulk = async (action: string) => {
    const ids = [...sel];
    if (action === "delete" && !confirm(`Delete ${ids.length} chains? History is kept.`)) return;
    if (action === "approved" && !confirm(`Approve and publish ${ids.length} chains? Each must pass validation; the rest are skipped.`)) return;
    setBusy(true); setMsg(undefined);
    try {
      const r = await api<{ done: string[]; skipped: Array<{ id: string; reason: string }>; published?: Published }>("/admin/spreads/bulk", { method: "POST", body: { ids, action } });
      setMsg({ ok: r.skipped.length === 0, text: `${action}: ${r.done.length} done${r.skipped.length ? `, ${r.skipped.length} skipped — ${r.skipped.map((s) => `${s.id} (${s.reason})`).join("; ")}` : ""}${r.published ? `. ${r.published.published} on the CDN.` : ""}` });
      setSel(new Set()); await load();
    } catch (e) { setMsg({ ok: false, text: (e as Error).message }); } finally { setBusy(false); }
  };

  if (err) return <div className="msg err">{err}</div>;
  if (!rows) return <p className="chrome">Loading…</p>;
  const shown = rows
    .filter((r) => filter === "all" || r.status === filter || (filter === "errors" && r.errors > 0))
    .filter((r) => topic === "all" || r.topic === topic);

  return (
    <>
      <h2>Chains</h2>
      <p className="sub">{rows.length} in the store · {rows.filter((r) => r.status === "approved").length} approved. Only approved, error-free chains are published.</p>
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
        {["all", "draft", "needs-work", "approved", "errors"].map((f) => (
          <button key={f} className={`btn${filter === f ? " primary" : ""}`} onClick={() => setFilter(f)}>{f}</button>
        ))}
        {/* A whole category at once: filter to it, tick the box in the header, then Back to draft or Delete. */}
        <select className="field" style={{ width: 190 }} value={topic} onChange={(e) => { setTopic(e.target.value); setSel(new Set()); }} aria-label="Topic">
          <option value="all">every topic · {rows.length}</option>
          {TOPICS.map((t) => <option key={t} value={t}>{t} · {rows.filter((r) => r.topic === t).length}</option>)}
        </select>
        <span style={{ flex: 1 }} />
        <form onSubmit={(e) => { e.preventDefault(); if (newId) nav(`/admin/spreads/${newId}?new=1`); }} style={{ display: "flex", gap: 8 }}>
          <input className="field" style={{ width: 200 }} value={newId} onChange={(e) => setNewId(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))} placeholder="new-chain-id" pattern="[a-z0-9-]+" />
          <button className="btn" disabled={!newId}>New by hand</button>
        </form>
      </div>
      {msg && <div className={`msg ${msg.ok ? "ok" : "err"}`}>{msg.text}</div>}
      {sel.size > 0 && (
        <div className="msg" style={{ display: "flex", gap: 8, alignItems: "center" }}>
          <span>{sel.size} selected</span>
          <span style={{ flex: 1 }} />
          <button className="btn primary" disabled={busy} onClick={() => bulk("approved")}>Approve &amp; publish</button>
          <button className="btn" disabled={busy} onClick={() => bulk("needs-work")}>Needs work</button>
          <button className="btn" disabled={busy} onClick={() => bulk("draft")}>Back to draft</button>
          <button className="btn danger" disabled={busy} onClick={() => bulk("delete")}>Delete</button>
          <button className="btn" disabled={busy} onClick={() => setSel(new Set())}>Clear</button>
        </div>
      )}
      <table className="tbl">
        <thead><tr>
          <th><input type="checkbox" checked={shown.length > 0 && shown.every((r) => sel.has(r.id))} onChange={(e) => setSel(e.target.checked ? new Set(shown.map((r) => r.id)) : new Set())} /></th>
          <th>Title</th><th>Topic · tags</th><th>Kind</th><th className="num">Beats</th><th className="num">Words</th><th>Checks</th><th>Status</th><th>Updated</th></tr></thead>
        <tbody>
          {shown.length === 0 && <tr><td colSpan={9} className="chrome">Nothing here.</td></tr>}
          {shown.map((r) => (
            <tr key={r.id} className="link" onClick={() => nav(`/admin/spreads/${r.id}`)}>
              <td onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={sel.has(r.id)} onChange={() => toggle(r.id)} /></td>
              <td><strong>{r.title || r.id}</strong>{r.inFeed === false && <span className="pill" style={{ marginLeft: 6 }}>held back</span>}<br /><span className="chrome">{r.hook}</span></td>
              <td>{r.topic}{r.tags?.length ? <><br /><span className="chrome">{r.tags.join(", ")}</span></> : ""}</td><td>{r.kind}</td><td className="num">{r.beats}</td><td className="num">{r.words}</td>
              <td>{r.errors > 0 ? <span className="pill needs-work">{r.errors} errors</span> : r.warnings > 0 ? <span className="chrome">{r.warnings} warnings</span> : <span className="chrome">clean</span>}</td>
              <td><span className={`pill ${r.status}`}>{r.status}</span>{r.publishedAt && <div className="chrome">on CDN</div>}</td>
              <td className="chrome">{fmtTime(r.updatedAt)}<br />v{r.version} · {r.updatedBy}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
